<script lang="ts">
  /**
   * 課題カテゴリ選択 UI。 一覧フィルタ / 作成モーダル / 詳細編集で共通利用する。
   * 選択肢末尾の「+ カテゴリを追加」からインライン入力でカテゴリマスタへ追加でき、
   * 追加後は一覧を再取得して新カテゴリを選択状態にする。
   */
  import { onMount } from 'svelte';
  import { problemCategoriesApi } from '@shared/api/problem-categories';
  import type { ProblemCategory } from '@shared/types/problem-category';

  interface Props {
    /** 選択中カテゴリ名。 '' は未選択 (placeholder)。 */
    value: string;
    onChange: (value: string) => void | Promise<void>;
    disabled?: boolean;
    /** 「+ カテゴリを追加」 を表示するか (編集権限に合わせる)。 */
    allowAdd?: boolean;
    /** 未選択時のラベル (例: '(未設定)' / 'カテゴリ (すべて)')。 */
    placeholderLabel?: string;
  }

  let {
    value,
    onChange,
    disabled = false,
    allowAdd = false,
    placeholderLabel = '(未設定)',
  }: Props = $props();

  const ADD_SENTINEL = '__add_category__';

  let categories = $state<ProblemCategory[]>([]);
  let loading = $state(true);
  let loadError = $state<string | null>(null);

  let adding = $state(false);
  let newName = $state('');
  let saving = $state(false);
  let changing = $state(false);
  let addError = $state<string | null>(null);
  let changeError = $state<string | null>(null);

  // select の表示値。 value prop に追従しつつ、 add sentinel 選択時は元に戻す。
  let selected = $state(value);
  $effect(() => {
    if (!changing) selected = value;
  });

  async function loadCategories(): Promise<void> {
    loading = true;
    loadError = null;
    try {
      const res = await problemCategoriesApi.list();
      categories = res.items;
    } catch (err) {
      loadError = err instanceof Error ? err.message : String(err);
      categories = [];
    } finally {
      loading = false;
    }
  }

  onMount(loadCategories);

  async function applyChange(next: string): Promise<void> {
    if (changing) return;
    const previous = value;
    selected = next;
    changing = true;
    changeError = null;
    try {
      await onChange(next);
    } catch (err) {
      selected = previous;
      changeError = err instanceof Error ? err.message : String(err);
    } finally {
      changing = false;
    }
  }

  function handleSelect(ev: Event): void {
    const next = (ev.currentTarget as HTMLSelectElement).value;
    if (next === ADD_SENTINEL) {
      selected = value; // sentinel は確定値にしない
      adding = true;
      addError = null;
      changeError = null;
      newName = '';
      return;
    }
    void applyChange(next);
  }

  function cancelAdd(): void {
    adding = false;
    newName = '';
    addError = null;
  }

  async function submitAdd(): Promise<void> {
    if (saving) return;
    const trimmed = newName.trim();
    if (trimmed === '') {
      addError = 'カテゴリ名を入力してください';
      return;
    }
    saving = true;
    addError = null;
    try {
      const created = await problemCategoriesApi.create(trimmed);
      await loadCategories();
      adding = false;
      newName = '';
      await applyChange(created.name);
    } catch (err) {
      addError = err instanceof Error ? err.message : String(err);
    } finally {
      saving = false;
    }
  }

  function onAddKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      void submitAdd();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      cancelAdd();
    }
  }
</script>

<div class="category-select">
  {#if adding}
    <div class="add-row">
      <input
        type="text"
        bind:value={newName}
        placeholder="新しいカテゴリ名"
        autocomplete="off"
        disabled={saving}
        onkeydown={onAddKey}
      />
      <button type="button" class="btn btn-primary" disabled={saving} onclick={submitAdd}>
        {saving ? '追加中…' : '追加'}
      </button>
      <button type="button" class="btn" disabled={saving} onclick={cancelAdd}>キャンセル</button>
    </div>
    {#if addError}<small class="error">{addError}</small>{/if}
  {:else}
    <select bind:value={selected} disabled={disabled || changing} onchange={handleSelect}>
      <option value="">{loading ? '読み込み中…' : placeholderLabel}</option>
      {#each categories as cat (cat.name)}
        <option value={cat.name}>{cat.name}</option>
      {/each}
      {#if allowAdd}
        <option value={ADD_SENTINEL}>+ カテゴリを追加</option>
      {/if}
    </select>
    {#if changing}
      <small class="hint">保存中…</small>
    {:else if loadError}
      <small class="error">{loadError}</small>
    {:else if changeError}
      <small class="error">{changeError}</small>
    {/if}
  {/if}
</div>

<style>
  .category-select {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .add-row {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-wrap: wrap;
  }
  .add-row input {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
  }
  small.hint,
  small.error {
    font-size: 0.75rem;
  }
  small.hint {
    color: #6b7280;
  }
  small.error {
    color: #b91c1c;
  }
</style>
